import { Button, Chip, IconButton, Tooltip, Typography } from "@mui/material";
import {
  GridCellParams,
  GridColDef,
  GridRenderCellParams,
} from "@mui/x-data-grid";
import { camelCaseToSentenceCase } from "../../../helpers/camelCaseToSentenceCase";
import {
  INCIDENT_TYPE_LABEL_MAP,
  normalizeIncidentTypeValue,
} from "../../Fleet/IncidentType";
import UpdateIncidentPopup from "../../../components/Fleet/UpdateIncidentPopup/UpdateIncidentPopup";
import IncidentStatusChip from "../../../components/Fleet/IncidentStatusChip/IncidentStatusChip";
import ConfirmActionButton from "../../../components/Fleet/ConfirmActionButton/ConfirmActionButton";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import IncidentNotesButton from "../../../components/Fleet/IncidentNotesButton/IncidentNotesButton";

const renderChargeStatusChip = (value: boolean | null | undefined) => {
  if (typeof value !== "boolean") {
    return <Chip label="No" color="default" />;
  }

  return (
    <Chip label={value ? "Yes" : "No"} color={value ? "success" : "default"} />
  );
};

export type IncidentStatus =
  | "notStarted"
  | "pending"
  | "completed"
  | "onHold"
  | "waitingForInvoice"
  | "null";

export const statusColors: Record<
  IncidentStatus,
  {
    label: string;
    color:
      | "default"
      | "primary"
      | "secondary"
      | "error"
      | "info"
      | "success"
      | "warning";
  }
> = {
  notStarted: { label: "Not Started", color: "default" },
  pending: { label: "Pending", color: "warning" },
  completed: { label: "Completed", color: "success" },
  onHold: { label: "On Hold", color: "info" },
  null: { label: "No Status", color: "default" },
  waitingForInvoice: { label: "Waiting For Invoice", color: "info" },
};

export const getIncidentsTableColumns = (
  processedIncidents: any[],
  handleExpandClick: (rowId: string) => void,
  handleOpenProofUpload: (incidentRow: any) => void,
  handleOpenProofPreview: (incidentRow: any) => void,
): GridColDef[] => [
  {
    field: "expand",
    headerName: "",
    width: 60,
    disableColumnMenu: true,
    sortable: false,
    renderCell: (params: GridCellParams) => {
      const { row } = params;
      const hasChildren = processedIncidents.some(
        (incident) => incident.incidentParentId === row.id,
      );
      if (row.level === 0 && hasChildren) {
        return (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleExpandClick(row.id);
            }}
          >
            {row.isExpanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        );
      }
      return null;
    },
  },
  {
    field: "notes",
    headerName: "",
    width: 50,
    disableColumnMenu: true,
    sortable: false,
    renderCell: (params: any) => (
      <IncidentNotesButton incidentId={params.row.id as string} />
    ),
  },
  {
    field: "openDate",
    headerName: "Open Date",
    width: 130,
    renderCell: (params: any) => (
      <Tooltip
        title={
          params.row.incidentStatus === "completed"
            ? `Time in service: ${params.row.totalCompletionTime}`
            : ""
        }
      >
        <Typography variant="body2" mt={2} ml={1}>
          {new Date(params.value as string).toLocaleDateString()}
        </Typography>
      </Tooltip>
    ),
  },
  {
    field: "incidentStatus",
    headerName: "Incident Status",
    width: 130,
    renderCell: (params: any) => (
      <IncidentStatusChip
        value={params.value as IncidentStatus}
        notes={params.row.notes}
      />
    ),
  },
  { field: "truckUnitNumber", headerName: "Truck Unit Number", width: 180 },
  { field: "trailerUnitNumber", headerName: "Trailer Unit Number", width: 180 },
  { field: "driverName", headerName: "Driver's Name", width: 130 },
  {
    field: "driverType",
    headerName: "Driver Type",
    width: 130,
    valueFormatter: (params: any) => camelCaseToSentenceCase(params as string),
  },
  {
    field: "complaint",
    headerName: "Complaint",
    width: 250,
    renderCell: (params: GridRenderCellParams) => {
      const { row } = params;
      const paddingLeft = row.level * 20;
      if (!row || typeof row.level === "undefined") {
        return <div style={{ paddingLeft }}>N/A</div>;
      }
      return (
        <div style={{ paddingLeft }}>
          {row.level === 0
            ? row.isExpanded
              ? row.complaint
              : row.totalComplaints
            : row.complaint}
        </div>
      );
    },
    cellClassName: (params: GridCellParams) => {
      const { row } = params;
      return row.level === 0 ? "parent-row" : "child-row";
    },
  },
  {
    field: "location",
    headerName: "Location",
    width: 130,
    valueFormatter: (params: any) => (params as string).toUpperCase(),
  },
  { field: "receivedBy", headerName: "Received By", width: 130 },
  {
    field: "incidentType",
    headerName: "Incident Type",
    width: 130,
    valueFormatter: (params: any) => {
      const incidentType = normalizeIncidentTypeValue(params as string);
      return incidentType
        ? (INCIDENT_TYPE_LABEL_MAP[incidentType] ??
            camelCaseToSentenceCase(incidentType))
        : "";
    },
  },
  {
    field: "finalCost",
    headerName: "Final Cost",
    width: 130,
    renderCell: (params: GridRenderCellParams) => {
      const { row } = params;
      if (row.level === 0) {
        if (row.isExpanded) {
          return "$" + row.finalCost || 0;
        } else {
          return "$" + row.totalFinalCost || 0;
        }
      } else {
        return "$" + row.finalCost || 0;
      }
    },
  },

  {
    field: "coordinates",
    headerName: "Coordinates",
    width: 180,
    renderCell: (params: any) => {
      const cleanedValue = params.value.replace(/[()]/g, "");
      const parts = cleanedValue.split(", ");
      const latitude = parts[0].replace(/,(\d{2,})/g, ".$1");
      const longitude = parts[1].replace(/,(\d{2,})/g, ".$1");
      if (!latitude || !longitude) {
        return "No coordinates provided";
      }

      return (
        <a
          href={`https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          View on Map
        </a>
      );
    },
  },
  {
    field: "proofs",
    headerName: "Proofs",
    width: 250,
    sortable: false,
    disableColumnMenu: true,
    renderCell: (params: GridRenderCellParams) => (
      <>
        <Button
          variant="contained"
          size="small"
          onClick={(event) => {
            event.stopPropagation();
            handleOpenProofUpload(params.row);
          }}
        >
          Upload Proofs
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={(event) => {
            event.stopPropagation();
            handleOpenProofPreview(params.row);
          }}
          sx={{ ml: 1 }}
        >
          Preview
        </Button>
      </>
    ),
  },
  {
    field: "rentCompanyName",
    headerName: "Rent Company Name",
    width: 180,
    valueFormatter: (params: any) => params || "N/A",
  },
  {
    field: "isCompanyCharged",
    headerName: "Company Charged",
    width: 170,
    renderCell: (params: any) => renderChargeStatusChip(params.value),
  },
  {
    field: "chargedCompanyAmount",
    headerName: "Company Charged Amount",
    width: 200,
    renderCell: (params: any) => {
      if (params.row.isCompanyCharged) {
        return "$" + (params.value || 0);
      }
      return "N/A";
    },
  },
  {
    field: "isDriverCharged",
    headerName: "Driver Charged",
    width: 150,
    renderCell: (params: any) => renderChargeStatusChip(params.value),
  },
  {
    field: "chargedDriverAmount",
    headerName: "Driver Charged Amount",
    width: 180,
    renderCell: (params: any) => {
      if (params.row.isDriverCharged) {
        return "$" + (params.value || 0);
      }
      return "N/A";
    },
  },
  {
    field: "update",
    headerName: "",
    width: 130,
    renderCell: (params: any) => <UpdateIncidentPopup incident={params.row} />,
  },
  {
    field: "delete",
    headerName: "",
    width: 130,
    renderCell: (params: any) => (
      <ConfirmActionButton
        id={params.row.id}
        entityType="Incident"
        actionType="delete"
        entityLabel="incident"
        queryKey="incidents"
        buttonLabel="Delete"
        buttonColor="error"
      />
    ),
  },
];
